// modules/otpVerification/OTPVerification.tsx
import React, { useState, useEffect } from 'react';
import { S3_API_BASE_URL } from '../../config/api';
import { dataUrlToObjectUrl } from '../../utils/dataUrl';

interface OTPVerificationProps {
    token: string;
    imageKey: string;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ token, imageKey }) => {
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
    const [timeLeft, setTimeLeft] = useState(300);

    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setMessage({ text: 'Link has expired. Please request a new one.', type: 'error' });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const showMessage = (text: string, type: 'success' | 'error') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 5000);
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length !== 6) {
            showMessage('Please enter the 6-digit verification code', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${S3_API_BASE_URL}/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, otp })
            });

            const result = await response.json();

            if (response.ok && result.url) {
                if (String(result.url).startsWith('data:')) {
                    const mediaUrl = dataUrlToObjectUrl(result.url);
                    window.open(mediaUrl, '_blank');
                    window.setTimeout(() => URL.revokeObjectURL(mediaUrl), 60_000);
                } else {
                    window.open(result.url, '_blank');
                }
                showMessage('✓ Access granted! Opening image in new tab...', 'success');

                // Close the page after 2 seconds or redirect to dashboard
                setTimeout(() => {
                    window.location.href = '/guest/dashboard';
                }, 2000);
            } else {
                showMessage(`✗ ${result.message || 'Invalid verification code'}`, 'error');
            }
        } catch (error) {
            showMessage('✗ Verification failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                    <h2 className="text-3xl font-bold text-gray-800">
                        🔐 Secure Access
                    </h2>
                    <div className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium text-gray-600">
                        ⏱️ {formatTime(timeLeft)}
                    </div>
                </div>

                {/* Image Info */}
                {imageKey && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-6 border border-gray-200">
                        <span className="font-semibold text-indigo-600">📷 Image:</span>
                        <span className="ml-2 text-gray-700 text-sm break-all">{imageKey.split('/').pop()}</span>
                    </div>
                )}

                {/* Message */}
                {message && (
                    <div className={`mb-4 p-3 rounded-lg ${message.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                        {message.text}
                    </div>
                )}

                {/* OTP Step */}
                <div className="space-y-4">
                        <div>
                            <h3 className="text-lg font-semibold text-indigo-600 mb-3">
                                Enter Verification Code
                            </h3>
                            <p className="text-sm text-gray-600 mb-3">
                                Enter the 6-digit code included in your secure campaign email.
                            </p>
                            <input
                                type="text"
                                placeholder="Enter 6-digit code"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                onKeyPress={(e) => e.key === 'Enter' && handleVerifyOTP()}
                                disabled={isLoading}
                                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 transition-colors text-center text-2xl tracking-widest disabled:bg-gray-100"
                                autoFocus
                            />
                        </div>
                        <button
                            onClick={handleVerifyOTP}
                            disabled={isLoading}
                            className={`w-full py-2 rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 ${isLoading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg text-white'
                                }`}
                        >
                            {isLoading ? 'Verifying...' : 'Verify & View Image'}
                        </button>
                    </div>

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-gray-200 text-center">
                    <p className="text-xs text-gray-500">
                        Secure link expires in 5 minutes • All access is logged for security
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OTPVerification;
